// Captures uncaught errors and stashes them in sessionStorage so FallbackUI
// can include file/line/msg in the crcnt redirect URL (the access-log entry
// is our crash-tracking pipeline). Stack frames here come from the minified
// bundle in production — utils/sourceMapResolver maps them back to the
// original .jsx using the .map files served alongside each chunk.

const STORAGE_KEY = "lastError";
const MAX_STACK_LEN = 800;
const MAX_MSG_LEN = 200;

// Pulls "file:line:col" out of the first non-empty frame of an Error.stack.
// Different browsers format slightly differently; the regex covers the common
// shapes (Chrome: "at fn (url:line:col)", Firefox: "fn@url:line:col").
const parseFirstFrame = (stack) => {
  if (!stack || typeof stack !== "string") return null;
  const lines = stack.split("\n");
  for (const line of lines) {
    const m = line.match(/(https?:\/\/[^\s)]+|[^\s(]+\.[jt]sx?):(\d+):(\d+)/);
    if (m) return { file: m[1], line: Number(m[2]), col: Number(m[3]) };
  }
  return null;
};

// React's componentStack only contains user component frames (never
// node_modules), and in dev mode each frame includes the source URL.
// Use this as the primary signal for "which file/line crashed" — it's
// immune to stack rewriting by SDK wrappers.
const parseFirstComponentFrame = (componentStack) => {
  if (!componentStack || typeof componentStack !== "string") return null;
  for (const line of componentStack.split("\n")) {
    const m = line.match(/(?:at|in)\s+([A-Z][A-Za-z0-9_.$]*)\s*\(([^)]+?):(\d+)(?::(\d+))?\)/);
    if (m) {
      return {
        component: m[1],
        file: m[2],
        line: Number(m[3]),
        col: m[4] != null ? Number(m[4]) : 0,
      };
    }
  }
  return null;
};

// Surrogate-safe truncation. JS strings are UTF-16; emojis and non-BMP chars
// use surrogate pairs (2 code units). A naive slice can land mid-pair and
// produce a lone surrogate, which makes encodeURIComponent throw URIError
// downstream — that would silently break the FallbackUI redirect.
const truncate = (s, n) => {
  if (typeof s !== "string" || s.length <= n) return s;
  let end = n;
  const code = s.charCodeAt(end - 1);
  if (code >= 0xD800 && code <= 0xDBFF) end -= 1;
  return s.slice(0, end);
};

const safeStringify = (val) => {
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
};

export const captureGlobalError = ({
  source,
  message,
  filename,
  lineno,
  colno,
  error,
  componentStack,
}) => {
  try {
    const stack = error && error.stack ? error.stack : null;
    // Prefer React's componentStack: it contains the *actual* user component
    // that crashed and is immune to SDK wrappers rewriting error.stack.
    // Fall back to error.stack and finally to browser-supplied filename.
    const compFrame = parseFirstComponentFrame(componentStack);
    const stackFrame = compFrame ? null : parseFirstFrame(stack);
    const file = (compFrame && compFrame.file) || (stackFrame && stackFrame.file) || filename || null;
    const line = (compFrame && compFrame.line) || (stackFrame && stackFrame.line) || lineno || null;
    const col = (compFrame && compFrame.col) != null
      ? compFrame.col
      : (stackFrame && stackFrame.col) != null
        ? stackFrame.col
        : (colno != null ? colno : null);

    const payload = {
      src: source || "unknown",
      msg: truncate(message || (error && error.message) || "", MAX_MSG_LEN),
      file: file ? truncate(file, MAX_MSG_LEN) : null,
      line,
      col,
      stack: stack ? truncate(stack, MAX_STACK_LEN) : null,
      componentStack: componentStack ? truncate(componentStack, MAX_STACK_LEN) : null,
      t: Date.now(),
      url: typeof window !== "undefined" ? window.location.href : null,
      ua: typeof navigator !== "undefined" ? navigator.userAgent : null,
    };

    sessionStorage.setItem(STORAGE_KEY, safeStringify(payload));
  } catch {
    // sessionStorage can throw in private mode / restricted webviews — swallow.
  }
};

export const getLastError = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearLastError = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

// Strips protocol/host/path so the URL only carries the chunk filename.
// e.g. "https://app.tispy.net/static/js/foo-cc485d.chunk.js" → "foo-cc485d.chunk.js"
const getBasename = (filePath) => {
  if (!filePath || typeof filePath !== "string") return filePath || "";
  const noQuery = filePath.split("?")[0].split("#")[0];
  const lastSlash = noQuery.lastIndexOf("/");
  return lastSlash >= 0 ? noQuery.slice(lastSlash + 1) : noQuery;
};

// CRA names lazy chunks after the source path with `/` and `.` swapped for `_`.
// e.g. "src_pages_QuickAccessBarViewComponentPage_AudioVideoChatView_jsx-...-cc485d.chunk.js"
//   → "src/pages/QuickAccessBarViewComponentPage/AudioVideoChatView.jsx"
// We pick the first module name (before the first `-`) since chunks may bundle
// multiple modules; the first one is usually what crashed.
const chunkNameToSource = (chunkBasename) => {
  if (!chunkBasename) return "";
  const stripped = chunkBasename.replace(/\.chunk\.js$/i, "").replace(/\.js$/i, "");
  // Drop the trailing content hash (e.g. "-cc485d" or "-c1ba3f12")
  const noHash = stripped.replace(/-[a-f0-9]{6,12}$/i, "");
  // Take the first module name before any `-` separator
  const firstModule = noHash.split("-")[0];
  if (!firstModule) return "";
  // Convert "_jsx" / "_tsx" / "_js" / "_ts" suffix back to extension
  const withExt = firstModule.replace(/_(jsx|tsx|ts|js)$/i, ".$1");
  // Remaining underscores become path separators
  return withExt.replace(/_/g, "/");
};

// React's componentStack looks like:
//   "\n    at AudioVideoChatView (...)\n    at QuickAccessBarPage (...)\n..."
// Pull the first component name — that's where the crash originated.
const firstComponentName = (componentStack) => {
  if (!componentStack || typeof componentStack !== "string") return "";
  const match = componentStack.match(/\b(?:at|in)\s+([A-Z][A-Za-z0-9_.$]*)/);
  return match ? match[1] : "";
};

// Pull all React component names from the stack in order (skipping host
// elements like "div" / "span" which start lowercase). The first name is the
// crashing component; the second is the one that rendered it (the "caller"),
// and so on up the tree.
const componentChain = (componentStack) => {
  if (!componentStack || typeof componentStack !== "string") return [];
  const names = [];
  const re = /\b(?:at|in)\s+([A-Z][A-Za-z0-9_.$]*)/g;
  let m;
  while ((m = re.exec(componentStack)) !== null) names.push(m[1]);
  return names;
};

// Fallback when componentStack isn't available (e.g. window.error /
// unhandledrejection events). Derive the component name from the file path
// using the project's convention: `src/pages/<Name>/...` or
// `src/components/<Name>/...`. Falls back to the basename without extension.
const componentFromPath = (file) => {
  if (!file || typeof file !== "string") return "";
  const m = file.match(/[/\\](?:pages|components)[/\\]([A-Z][A-Za-z0-9_]*)/);
  if (m) return m[1];
  const noQuery = file.split("?")[0].split("#")[0];
  const base = noQuery.split(/[/\\]/).pop() || "";
  const stem = base.replace(/\.[jt]sx?$/, "");
  // Skip generic names like "index" — they tell us nothing.
  return /^[A-Z]/.test(stem) ? stem : "";
};

// Replace newlines/tabs with spaces before URL-encoding so the encoded form
// stays readable in logs. encodeURIComponent handles &, =, #, ?, %, etc.
const cleanForEncode = (s) => {
  if (!s) return "";
  return String(s).replace(/[\r\n\t]+/g, " ").trim();
};

// Defensive: even with surrogate-safe truncation, mangled inputs from third-
// party stack frames could still trip encodeURIComponent. Returning "" on
// failure keeps the redirect URL valid rather than throwing through useEffect.
const encodeValue = (s, max) => {
  try {
    return encodeURIComponent(cleanForEncode(truncate(s, max)));
  } catch {
    return "";
  }
};

// Builds the short URL fragment we append to the FallbackUI redirect. Each
// value is URL-encoded so special chars (spaces, :, /, etc.) don't corrupt
// the query string or break server-side log parsers.
// Output shape (resolved):    &source=src%2Fpath%2FFile.jsx&line=7&col=11&msg=...
// Output shape (unresolved):  &source=...&component=Foo&file=chunk.js&line=1558&col=11&msg=...
export const buildErrorQueryString = (err) => {
  if (!err) return "";
  const parts = [];
  const isResolvedSource =
    err.file && /[/\\]/.test(err.file) && !/\.chunk\.js$/i.test(err.file);
  const chunkFile = isResolvedSource ? null : getBasename(err.file);
  const source = isResolvedSource ? err.file : chunkNameToSource(chunkFile);
  const chain = componentChain(err.componentStack);
  // Prefer React's componentStack (most accurate). Fall back to deriving
  // the component name from the source file path so window.error /
  // unhandledrejection events also get a useful component= field.
  const component = chain[0] || firstComponentName(err.componentStack) || componentFromPath(err.file);
  // The component that rendered the crashing one — only available when we
  // have a real componentStack.
  const caller = chain[1] || "";
  if (source) parts.push(`source=${encodeValue(source, 200)}`);
  if (component) parts.push(`component=${encodeValue(component, 100)}`);
  if (caller) parts.push(`caller=${encodeValue(caller, 100)}`);
  if (chunkFile) parts.push(`file=${encodeValue(chunkFile, 200)}`);
  if (err.line) parts.push(`line=${err.line}`);
  if (err.col) parts.push(`col=${err.col}`);
  if (err.msg) parts.push(`msg=${encodeValue(err.msg, 150)}`);
  // App version (baked at build time via prebuild script in package.json) so
  // we know which release the crash came from when reading nginx logs.
  const version = process.env.REACT_APP_VERSION;
  if (version) parts.push(`version=${encodeValue(version, 50)}`);
  return parts.length ? `&${parts.join("&")}` : "";
};
